import MentionIcon from '@atlaskit/icon/glyph/mention';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import { useFlags } from '@databases/flags';
import usePanel from '@databases/panel/usePanel';
import { useCollabManager } from '@databases/utils/hooks/useCollabManager';
import React from 'react';
import { useIntl } from 'react-intl-next';

import { getConfluenceService } from '@canvas/atlassian-service/confluence';
import { Mention } from '@canvas/atlassian-service/confluence/types';

import { messages } from './i18n';
import { buildFlagForMentions } from './mentionBuilder';
import { showBannerForRestrictedUsers } from './showRestrictionsBanner';
import {
  UseMentionNotificationProps,
  CreateMentionNotificationProps,
  CheckContentPermissionsProps,
  User,
  IdentifyNotificationEligibleUsersProps,
  MentionFlagType,
} from './types';

export const checkContentPermissions = async ({
  accountIds,
  contentId,
}: CheckContentPermissionsProps) => {
  const service = getConfluenceService();
  const permissions = await service.getContentPermissions({
    accountIds,
    contentId,
  });

  return permissions;
};

export const identifyNotificationEligibleUsers = async ({
  contentId,
  mentions,
}: IdentifyNotificationEligibleUsersProps) => {
  // For mention notification creation
  const usersToMention: Mention[] = [];
  // For flag display
  const usersWithoutAccess: User[] = [];
  const usersWithAccess: User[] = [];

  const usersWithPermissionsInfo = await checkContentPermissions({
    accountIds: mentions.map((mention) => mention.mentionedUserAccountId),
    contentId,
  });

  const mentionsWithPermissionsInfo = usersWithPermissionsInfo.map(
    (userWithPermissionsInfo) => {
      // Each `userWithPermissionsInfo` has a corresponding mention, lookup will be guaranteed
      const currentMention = mentions.find(
        (mention) => mention.mentionedUserAccountId === userWithPermissionsInfo.accountId
      )!;
      return {
        mentionLocalId: currentMention.mentionLocalId,
        mentionedUserAccountId: currentMention.mentionedUserAccountId,
        displayName: currentMention.displayName,
        profilePicture: userWithPermissionsInfo?.profilePicture?.path,
        permissions: {
          isSpaceRestricted: userWithPermissionsInfo.isSpaceRestricted,
          isParentDatabaseRestricted: userWithPermissionsInfo.isParentDatabaseRestricted,
          userHasAccessToContent: userWithPermissionsInfo.userHasAccessToContent,
        },
      };
    }
  );

  for (const mention of mentionsWithPermissionsInfo) {
    usersToMention.push({
      mentionLocalId: mention.mentionLocalId,
      mentionedUserAccountId: mention.mentionedUserAccountId,
      notificationAction: mention.permissions.userHasAccessToContent
        ? 'NOTIFY'
        : 'DONT_NOTIFY',
    });

    if (!mention.permissions.userHasAccessToContent) {
      usersWithoutAccess.push({
        mentionLocalId: mention.mentionLocalId,
        mentionedUserAccountId: mention.mentionedUserAccountId,
        displayName: mention.displayName,
        profilePicture: mention.profilePicture,
        permissions: mention.permissions,
      });
    } else {
      usersWithAccess.push({
        mentionLocalId: mention.mentionLocalId,
        mentionedUserAccountId: mention.mentionedUserAccountId,
        displayName: mention.displayName,
        profilePicture: mention.profilePicture,
        permissions: mention.permissions,
      });
    }
  }

  return { usersToMention, usersWithoutAccess, usersWithAccess };
};

export const useMentionNotification = ({ contentId }: UseMentionNotificationProps) => {
  const { formatMessage } = useIntl();
  const { addFlag } = useFlags();
  const { showPanel } = usePanel();
  const service = getConfluenceService();
  const isMentionsNotificationsFlagEnabled = useBooleanFeatureFlag(
    'databases-frontend-mentions-notifications-flag'
  );
  const analyticsClient = useAnalytics();
  const collabManager = useCollabManager(contentId);
  const ydb = collabManager?.database.toJSON();
  // Users are not allowed to access field settings in a locked structure db
  const isLocked = ydb?.isLocked || false;

  const createMentionNotification = async ({
    mentions,
    fieldId,
  }: CreateMentionNotificationProps) => {
    const { usersToMention, usersWithoutAccess, usersWithAccess } =
      await identifyNotificationEligibleUsers({
        contentId,
        mentions,
      });

    try {
      await service.createMentionNotification({
        contentId,
        mentions: usersToMention,
      });

      if (isMentionsNotificationsFlagEnabled) {
        const flagData = buildFlagForMentions({
          formatMessage,
          usersWithAccess,
          usersWithoutAccess,
          isLocked,
          showPanel,
          fieldId,
          analyticsClient,
          contentId,
        });

        flagData.forEach(addFlag);

        analyticsClient.sendMentionNotificationFlagViewedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          type: MentionFlagType.SUCCESS,
        });
      }
    } catch (err) {
      if (isMentionsNotificationsFlagEnabled) {
        addFlag({
          title: formatMessage(messages['mention-notification.flag-error-title']),
          description: formatMessage(
            messages['mention-notification.flag-error-description']
          ),
          icon: <MentionIcon label="" />,
        });

        analyticsClient.sendMentionNotificationFlagViewedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          type: MentionFlagType.ERROR,
        });
      } else {
        throw new Error(formatMessage(messages['mention-notification.snackbar-error']));
      }
    }

    if (!isMentionsNotificationsFlagEnabled) {
      showBannerForRestrictedUsers({
        users: usersWithoutAccess,
        formatMessage,
      });
    }
  };

  return { createMentionNotification };
};
